import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  DocumentIcon,
  PhotoIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  ArchiveBoxIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

const DocumentTableRow = ({
  document,
  onEdit,
  onDelete,
  onDownload,
  onView,
  onToggleShare,
}) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      window.document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    const fileName = document.file_name || document.document?.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (!ext) return <DocumentIcon className="h-5 w-5 text-succession-text-secondary" />;

    switch (ext) {
      case 'pdf':
        return <DocumentArrowDownIcon className="h-5 w-5 text-succession-text-secondary" />;
      case 'doc':
      case 'docx':
        return <DocumentTextIcon className="h-5 w-5 text-succession-text-secondary" />;
      case 'xls':
      case 'xlsx':
        return <TableCellsIcon className="h-5 w-5 text-succession-text-secondary" />;
      case 'zip':
      case 'rar':
      case '7z':
        return <ArchiveBoxIcon className="h-5 w-5 text-succession-text-secondary" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
      case 'fig':
        return <PhotoIcon className="h-5 w-5 text-succession-text-secondary" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-succession-text-secondary" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      financial: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      legal: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      operational: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      hr: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
    return colors[category] || colors.other;
  };

  const fileName = document.file_name || document.document?.name || intl.formatMessage({ id: 'succession.vault.unknown_file' });
  const fileSize = document.file_size || document.document?.file_size || 0;
  const isShared = document.shared_with_negotiating || false;

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleMenuAction = (action) => {
    setMenuOpen(false);
    action();
  };

  return (
    <tr className="border-b border-succession-border hover:bg-succession-hover-bg transition-colors">
      {/* Title Column */}
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">{getFileIcon()}</div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-succession-text-primary truncate">
              {document.title}
            </div>
            <div className="text-xs text-succession-text-secondary truncate">
              {fileName} • {formatFileSize(fileSize)}
            </div>
          </div>
        </div>
      </td>

      {/* Description Column */}
      <td className="p-4">
        <div className="space-y-1">
          {document.description && (
            <p className="text-sm text-succession-text-secondary line-clamp-2">
              {document.description}
            </p>
          )}
          {document.source_type === 'sprint_item' && document.sprint_name && (
            <p className="text-xs text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.vault.sprint_label' }, { name: document.sprint_name })}
            </p>
          )}
          {!document.description && (!document.source_type || document.source_type !== 'sprint_item') && (
            <p className="text-sm text-succession-text-secondary">—</p>
          )}
        </div>
      </td>

      {/* Category Column */}
      <td className="p-4">
        <span className={`px-2 py-0.5 text-xs rounded-md border-0 ${getCategoryColor(document.category || 'other')}`}>
          {document.category || intl.formatMessage({ id: 'succession.vault.cat_other' })}
        </span>
      </td>

      {/* Tags Column */}
      <td className="p-4">
        <div className="flex flex-wrap gap-1.5 max-w-[200px]">
          {document.tags && document.tags.slice(0, 2).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md"
            >
              {tag}
            </span>
          ))}
          {document.tags && document.tags.length > 2 && (
            <span className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md">
              +{document.tags.length - 2}
            </span>
          )}
        </div>
      </td>

      {/* Sharing Column */}
      <td className="p-4">
        <div className="flex items-center justify-center gap-2">
          <div className="relative inline-block w-10 h-5">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => {
                e.stopPropagation();
                onToggleShare(document, e.target.checked);
              }}
              className="sr-only"
            />
            <div
              className={`absolute inset-0 rounded-full transition-all duration-200 ${
                isShared
                  ? 'bg-succession-primary'
                  : 'bg-succession-bg-secondary'
              }`}
            />
            <div
              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                isShared ? 'translate-x-5' : ''
              }`}
            />
          </div>
          {isShared && (
            <ShareIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          )}
        </div>
      </td>

      {/* Actions Column */}
      <td className="p-4">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="h-8 w-8 flex items-center justify-center text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
            title={intl.formatMessage({ id: 'succession.vault.action_view' })}
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            className="h-8 w-8 flex items-center justify-center text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
            title={intl.formatMessage({ id: 'succession.vault.action_download' })}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
          <div className="relative" ref={menuRef}>
            <button
              onClick={handleMenuToggle}
              className="h-8 w-8 flex items-center justify-center text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-8 z-50 w-48 bg-succession-bg-primary border border-succession-border rounded-md shadow-succession-lg py-1">
                <button
                  onClick={() => handleMenuAction(onEdit)}
                  className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 transition-theme duration-theme"
                >
                  <PencilIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.vault.action_edit' })}
                </button>
                {document.can_delete && (
                  <>
                    <div className="border-t border-succession-border my-1" />
                    <button
                      onClick={() => handleMenuAction(onDelete)}
                      className="w-full px-4 py-2 text-left text-sm text-succession-text-primary hover:bg-succession-hover-bg flex items-center gap-2 text-red-600 dark:text-red-400 transition-theme duration-theme"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {intl.formatMessage({ id: 'succession.vault.action_delete' })}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

DocumentTableRow.propTypes = {
  document: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onToggleShare: PropTypes.func.isRequired,
};

export default DocumentTableRow;
